/** Pure Organizations (حساب‌های سازمان) display mapping — no I/O. C9A. */

import { cmsStrings } from "../terminology";
import type { StatusTone } from "./overview-ops";

export type OrganizationListRow = {
  id: string;
  code: string;
  displayName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type OrganizationSortKey = "name" | "code" | "status" | "updated";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export const ORGANIZATIONS_PAGE_NOTE =
  "حساب‌های سازمان فهرست همهٔ مستأجرها است. فقط مالک پلتفرم می‌تواند حساب جدید بسازد. تعلیق و بایگانی در این موج نیست.";

const ui = cmsStrings.organizationsUi;

export const ORGANIZATION_STATUS_LABELS: Record<string, string> = {
  draft: ui.statusDraft,
  active: ui.statusActive,
  suspended: ui.statusSuspended,
  archived: ui.statusArchived,
};

export function organizationStatusDisplay(status: string): StatusDisplay {
  const label = ORGANIZATION_STATUS_LABELS[status] ?? status;
  if (status === "active") {
    return { label, tone: "success", detail: "حساب فعال است" };
  }
  if (status === "draft") {
    return { label, tone: "info", detail: "حساب هنوز فعال نیست" };
  }
  if (status === "suspended") {
    return { label, tone: "danger", detail: "نوشتن در این سازمان مسدود است" };
  }
  if (status === "archived") {
    return { label, tone: "neutral", detail: "حساب بایگانی شده است" };
  }
  return { label, tone: "neutral" };
}

export function matchesOrganizationQuery(row: OrganizationListRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.displayName, row.code, row.id, row.status, ORGANIZATION_STATUS_LABELS[row.status] ?? ""]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesOrganizationFilter(row: OrganizationListRow, filter: string): boolean {
  if (filter === "all") return true;
  return row.status === filter;
}

export function filterAndSortOrganizations(
  rows: OrganizationListRow[],
  opts: { query: string; filter: string; sort: OrganizationSortKey },
): OrganizationListRow[] {
  const filtered = rows.filter(
    (r) => matchesOrganizationQuery(r, opts.query) && matchesOrganizationFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "code") return a.code.localeCompare(b.code, "en");
    if (opts.sort === "status") return a.status.localeCompare(b.status);
    if (opts.sort === "updated") return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
    return a.displayName.localeCompare(b.displayName, "fa");
  });
  return sorted;
}

export type RelatedOrganizationsLink = { id: string; label: string; to: string };

export function relatedOrganizationsLinks(): RelatedOrganizationsLink[] {
  return [
    { id: "memberships", label: cmsStrings.nav.memberships, to: "/memberships" },
    { id: "entitlements", label: cmsStrings.nav.entitlements, to: "/entitlements" },
    { id: "sites", label: cmsStrings.nav.sitesAndGroups, to: "/sites" },
    { id: "audit", label: cmsStrings.nav.audit, to: "/audit" },
  ];
}
